import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Paper,
} from '@mui/material';
import {
  MdClose,
  MdSettings,
  MdOutlineDashboardCustomize,
  MdAbc,
  MdQrCode,
  MdPolicy,
  MdPayments,
  MdCalendarMonth,
} from 'react-icons/md';
import GoogleCalendarSettings from './GoogleCalendarSettings';
import QRCode from 'react-qr-code';
import CopyToClipboard from 'react-copy-to-clipboard';
import { RWebShare } from 'react-web-share';
import { successMessage } from '../logic/error/action';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

const SettingsSidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.auth);
  const [activeSection, setActiveSection] = useState('main');
  const [activeTab, setActiveTab] = useState(0);

  const settingsOptions = [
    {
      id: 'services',
      title: 'Services',
      description: 'Manage your business services, pricing, and availability',
      icon: <MdSettings size={24} />,
      path: '/admin/service',
      color: '#5c6ac4',
    },
    {
      id: 'fields',
      title: 'Custom Fields',
      description: 'Create and manage custom fields for your business forms',
      icon: <MdOutlineDashboardCustomize size={24} />,
      path: '/admin/field',
      color: '#8b5cf6',
    },
    {
      id: 'templates',
      title: 'Message Templates',
      description: 'Customize email and SMS templates for your communications',
      icon: <MdAbc size={24} />,
      path: '/admin/templates',
      color: '#ec4899',
    },
    {
      id: 'plans',
      title: 'Plans & Pricing',
      description: 'View plans, upgrade subscription, and billing history',
      icon: <MdPayments size={24} />,
      path: '/admin/pricePlan',
      color: '#0ea5e9',
    },
    {
      id: 'google',
      title: 'Google Calendar',
      description: 'Connect Google for Meet links and calendar events',
      icon: <MdCalendarMonth size={24} />,
      path: null,
      color: '#4285F4',
      action: 'google',
    },
  ];

  const bookingUrl = `https://taa.dukaani.in/business/${selector?.user?.name
    ?.trim()
    .replaceAll(' ', '_')}/${selector?.user?.business_id}/book-appointment`;

  const handleOptionClick = (option) => {
    if (option.action === 'google') {
      setActiveSection('google');
      return;
    }
    if (option.path) {
      navigate(option.path);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
          maxWidth: '700px',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid #e0e0e0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Settings
            </Typography>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <MdClose />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {activeSection === 'main' && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1f2937' }}>
                Customize your business settings
              </Typography>

              {/* Settings Options */}
              <List sx={{ mb: 3 }}>
                {settingsOptions.map((option) => (
                  <ListItem key={option.id} disablePadding sx={{ mb: 2 }}>
                    <Paper
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          borderColor: option.color,
                        },
                      }}
                      onClick={() => handleOptionClick(option)}
                    >
                      <ListItemButton>
                        <ListItemIcon sx={{ color: option.color }}>
                          {option.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={option.title}
                          secondary={option.description}
                          primaryTypographyProps={{
                            fontWeight: 600,
                            color: '#1f2937',
                          }}
                          secondaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItemButton>
                    </Paper>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 3 }} />

              {/* Additional Options */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                Additional Options
              </Typography>

              <List>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <Paper
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: '#10b981',
                      },
                    }}
                    onClick={() => setActiveSection('qr')}
                  >
                    <ListItemButton>
                      <ListItemIcon sx={{ color: '#10b981' }}>
                        <MdQrCode size={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary="QR Code"
                        secondary="Share your booking QR code with customers"
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#1f2937',
                        }}
                        secondaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  </Paper>
                </ListItem>

                <ListItem disablePadding>
                  <Paper
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: '#3b82f6',
                      },
                    }}
                    onClick={() => setActiveSection('terms')}
                  >
                    <ListItemButton>
                      <ListItemIcon sx={{ color: '#3b82f6' }}>
                        <MdPolicy size={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Terms & Conditions"
                        secondary="View privacy policy and terms of service"
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: '#1f2937',
                        }}
                        secondaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  </Paper>
                </ListItem>
              </List>
            </>
          )}

          {/* Google Calendar */}
          {activeSection === 'google' && (
            <Box>
              <Button
                onClick={() => setActiveSection('main')}
                sx={{ mb: 3, textTransform: 'none' }}
              >
                ← Back to Settings
              </Button>
              <GoogleCalendarSettings />
            </Box>
          )}

          {/* QR Code Section */}
          {activeSection === 'qr' && (
            <Box>
              <Button
                onClick={() => setActiveSection('main')}
                sx={{ mb: 3, textTransform: 'none' }}
              >
                ← Back to Settings
              </Button>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                QR Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Share your QR code for booking an Appointment
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: '8px',
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      component="a"
                      href={bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {bookingUrl}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <RWebShare
                      data={{
                        text: 'Hello, Click this link for booking Appointment.',
                        url: bookingUrl,
                        title: 'Share link',
                      }}
                      disableNative={false}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<MdQrCode />}
                        sx={{ textTransform: 'none' }}
                      >
                        Share
                      </Button>
                    </RWebShare>
                    <CopyToClipboard
                      onCopy={() => successMessage(dispatch, 'URL Copied')}
                      text={bookingUrl}
                    >
                      <Button
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                      >
                        Copy Link
                      </Button>
                    </CopyToClipboard>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: '#667eea',
                    borderRadius: '12px',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: 'white',
                      borderRadius: '8px',
                      p: 2,
                    }}
                  >
                    <QRCode size={200} value={bookingUrl} />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Terms & Conditions Section */}
          {activeSection === 'terms' && (
            <Box>
              <Button
                onClick={() => setActiveSection('main')}
                sx={{ mb: 3, textTransform: 'none' }}
              >
                ← Back to Settings
              </Button>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                Terms & Conditions
              </Typography>

              <Tabs className="tabs">
                <TabList className="flex items-center justify-center relative gap-10 pb-6">
                  <Tab>
                    <button
                      onClick={() => setActiveTab(0)}
                      className={`font-bold text-xl cursor-pointer ${
                        activeTab === 0 ? 'border-b-2 border-blue-600' : ''
                      }`}
                    >
                      Privacy Policy
                    </button>
                  </Tab>
                  <Tab>
                    <button
                      onClick={() => setActiveTab(1)}
                      className={`font-bold text-xl cursor-pointer ${
                        activeTab === 1 ? 'border-b-2 border-blue-600' : ''
                      }`}
                    >
                      Terms & Conditions
                    </button>
                  </Tab>
                </TabList>
                <TabPanel>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto', pr: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      This Privacy Policy ("Policy") applies to the [The Appointment App] ("Application")
                      owned and operated by [Digital Amigos], a company registered in India. This Policy describes
                      how the Application collects, uses, and discloses personal information of its users ("Users").
                    </Typography>
                    <ol className="list-decimal list-inside mb-8 space-y-2">
                      <li>
                        <strong>Collection of Information:</strong> The Application may collect personal information from Users, including but not limited to name, email address, phone number, and location. The Application may also collect non-personal information, such as usage data and device information.
                      </li>
                      <li>
                        <strong>Use of Information:</strong> The Application uses personal information to provide its services to Users, improve the Application, and communicate with Users. The Application may also use non-personal information for analytics and research purposes.
                      </li>
                      <li>
                        <strong>Disclosure of Information:</strong> The Application does not disclose personal information to third parties, except as required by law or to provide its services to Users. The Application may disclose non-personal information to third parties for analytics and research purposes.
                      </li>
                      <li>
                        <strong>Security:</strong> The Application uses industry-standard security measures to protect personal information from unauthorized access, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
                      </li>
                      <li>
                        <strong>Cookies:</strong> The Application may use cookies or similar technologies to collect non-personal information and improve the user experience. Users can disable cookies in their browser settings, but this may limit their use of the Application.
                      </li>
                      <li>
                        <strong>Third-Party Services:</strong> The Application may use third-party services, such as analytics and advertising providers, that may collect personal information from Users. Users should review the privacy policies of these third-party services.
                      </li>
                      <li>
                        <strong>Children's Privacy:</strong> The Application is not intended for children under the age of 13. The Application does not knowingly collect personal information from children under the age of 13.
                      </li>
                      <li>
                        <strong>Changes to Policy:</strong> The Application may update this Policy from time to time. Users will be notified of any changes by email or through the Application. The User's continued use of the Application following any changes constitutes acceptance of the new Policy.
                      </li>
                      <li>
                        <strong>Contact Us:</strong> Users may contact the Application at any time with questions or concerns about this Policy or the Application's privacy practices.
                      </li>
                      <li>
                        <strong>Governing Law:</strong> This Policy shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with this Policy shall be resolved in accordance with the dispute resolution process outlined in the Terms and Conditions.
                      </li>
                      <li>
                        <strong>Phonebook Access:</strong> Clearly state that the appointment application may request access to a user's phonebook contacts in order to facilitate appointment scheduling, or any other related activities and explain how this data will be used, such as to retrieve contact names and phone numbers for appointment reminders or notifications.
                      </li>
                      <li>
                        <strong>Collection and Use of Location Data:</strong> The application may collect and use your location data when you grant access to your device's location services at the time of login. This location data may be used to verify your geographical location for login authentication purposes or to provide personalized content based on your location. The location data collected may include your device's GPS coordinates, Wi-Fi network information, cellular network information, or IP address.
                      </li>
                    </ol>
                  </Box>
                </TabPanel>
                <TabPanel>
                  <Box sx={{ maxHeight: '400px', overflow: 'auto', pr: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      These Terms and Conditions ("Agreement") govern the use of the [The Appointment App]
                      ("Application"), which is owned and operated by [Digital Amigos], a company registered in India.
                      By using this Application, you ("User") agree to be bound by these terms and conditions.
                    </Typography>
                    <ol className="list-decimal list-inside mb-8 space-y-2">
                      <li>
                        <strong>Acceptance of Terms:</strong> By accessing or using the Application, you agree to be bound by the terms and conditions of this Agreement. If you do not agree to these terms, you may not use the Application.
                      </li>
                      <li>
                        <strong>Use of Application:</strong> The User acknowledges and agrees that the Application is intended for personal use only and is not to be used for commercial purposes. The User agrees to use the Application in a lawful and responsible manner, and to comply with all applicable laws and regulations.
                      </li>
                      <li>
                        <strong>Intellectual Property Rights:</strong> The User acknowledges and agrees that all content and materials on the Application, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are protected by copyright, trademark, or other proprietary rights and laws.
                      </li>
                      <li>
                        <strong>Privacy Policy:</strong> The User agrees to the Application's Privacy Policy, which is incorporated herein by reference. The Privacy Policy sets out the User's rights and obligations with respect to the collection, use, and disclosure of personal information.
                      </li>
                      <li>
                        <strong>User Conduct:</strong> The User agrees not to use the Application in any manner that is illegal, harmful, or interferes with the use of the Application by others. The User agrees not to transmit any content that is obscene, defamatory, or infringes upon the rights of others.
                      </li>
                      <li>
                        <strong>Termination:</strong> The Company reserves the right to terminate the User's use of the Application at any time without notice or liability, for any reason including, but not limited to, violation of these terms and conditions.
                      </li>
                      <li>
                        <strong>Disclaimer of Warranties:</strong> The Application is provided on an "as is" basis, without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.
                      </li>
                      <li>
                        <strong>Limitation of Liability:</strong> In no event shall the Company or its affiliates be liable to the User or any third party for any damages, including without limitation, direct, indirect, incidental, consequential, special, or punitive damages, arising out of or in connection with the use of the Application.
                      </li>
                      <li>
                        <strong>Indemnification:</strong> The User agrees to indemnify and hold the Company and its affiliates, officers, directors, and employees harmless from any claims, damages, or expenses, including attorney's fees, arising out of or in connection with the User's use of the Application.
                      </li>
                      <li>
                        <strong>Governing Law and Jurisdiction:</strong> This Agreement shall be governed by and construed in accordance with the laws of India. The User agrees that any legal action arising out of or in connection with this Agreement shall be brought in the courts of Indore, India.
                      </li>
                      <li>
                        <strong>Changes to Terms and Conditions:</strong> The Company reserves the right to change these terms and conditions at any time without notice. The User's continued use of the Application following any changes constitutes acceptance of the new terms and conditions.
                      </li>
                      <li>
                        <strong>Entire Agreement:</strong> This Agreement constitutes the entire agreement between the User and the Company with respect to the use of the Application and supersedes all prior or contemporaneous communications and proposals, whether oral or written, between the User and the Company.
                      </li>
                      <li>
                        <strong>Data Usage:</strong> Specify that the phonebook contact information accessed by the appointment application will only be used for the purpose of scheduling appointments or for any app related purposes and will not be shared with third parties or used for any other purposes without the user's explicit consent.
                      </li>
                      <li>
                        <strong>Location Access:</strong> By using the application and granting access to your location, you acknowledge and agree that the application may collect and use your location data for the purpose of providing location-based features, such as identifying your geographical location for login authentication or providing personalized content based on your location.
                      </li>
                      <li>
                        <strong>Cancellation:</strong> Please note that all purchases are final, and we do not offer cancellations or refunds once a plan is purchased. We strongly recommend carefully evaluating your decision before proceeding with the purchase. If you have any concerns or questions about the plan, please contact our customer support team prior to making the purchase. Your understanding regarding our cancellation policy is greatly appreciated, and we are committed to providing exceptional service.
                      </li>
                      <li>
                        <strong>Refund Policy:</strong> Please note that all purchases are non-refundable. We recommend carefully considering your needs before making a purchase. We strive to provide comprehensive information to assist in your decision-making process. If you have any inquiries or concerns, please contact our customer support team prior to completing your purchase. We appreciate your understanding of our refund policy as we aim to deliver exceptional products and services to ensure a positive customer experience.
                      </li>
                      <li>
                        <strong>Return Policy:</strong> Due to the nature of our intangible and digital services, we do not have a return policy in place. Our team is dedicated to providing high-quality digital solutions and exceptional customer service. If you have any questions or concerns about our services, please contact our customer support team for assistance. We appreciate your understanding and trust in our services. Thank you for choosing our company.
                      </li>
                      <li>
                        <strong>Payment Policy:</strong> Our payment terms and conditions include accepting payments through various methods, payment due dates from the day after the current subscription period ends, customers being responsible for applicable taxes and fees, payments made in Indian currency, prompt payment for uninterrupted service, and resolving disputes through our customer support team. We treat payment-related information with utmost confidentiality and comply with applicable laws and regulations.
                      </li>
                    </ol>
                  </Box>
                </TabPanel>
              </Tabs>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SettingsSidebar;

